// src/core/jurisdiction.ts
//
// Decides whether the EU/UK cookie consent banner needs to be shown.
//
// We use the browser's IANA timezone as a proxy for jurisdiction — this is
// zero-deps, zero-latency, requires no IP lookup, and is privacy-friendly
// (timezone is already part of every fingerprint). Accuracy is good enough
// for the legal question "should we ask before setting analytics cookies":
//
// - All EU member states resolve to `Europe/<city>` IANA names. ✓
// - UK (UK GDPR + PECR), Iceland, Liechtenstein, Norway, Switzerland (EFTA,
//   GDPR-aligned in practice) — all `Europe/*` or under `Atlantic/Reykjavik`. ✓
// - A small handful of `Europe/*` countries are NOT under GDPR (Russia,
//   Turkey, Belarus, Ukraine, Vatican, Andorra, San Marino, Monaco). False
//   positives: they see a banner they don't strictly need. Tolerable.
// - Everywhere else (US, Canada, LatAm, most of Asia, Africa, Oceania) skips
//   the banner.
//
// Caveats accepted:
// - VPN users: timezone reflects what their browser reports, which usually
//   tracks their actual locale (set at OS install) rather than the VPN exit.
//   Even when it doesn't, this is a reasonable proxy for the user's intent.
// - Travelers: an EU resident on holiday in the US sees the US behavior for
//   the duration. Their localStorage consent (if previously decided) is
//   preserved across sessions, so this only matters on a fresh device.
// - Atlantic/Reykjavik (Iceland, EEA member) is explicitly included.
//
// If you ever need IP-based geofencing instead, route through Azure Front
// Door and read the X-Azure-ClientIP geo headers — but that requires moving
// off plain Container Apps. Out of scope for v1.

export function requiresCookieConsent(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return true; // safer default if the API didn't return a zone
    if (tz.startsWith('Europe/')) return true;
    if (tz === 'Atlantic/Reykjavik') return true; // Iceland (EEA)
    return false;
  } catch {
    // Any unexpected error → safer default is "show the banner"
    return true;
  }
}
